import { CalendarDays } from "lucide-react";
import { useState, type ReactNode } from "react";

import { DayField } from "@/components/dates/DayField";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { parseDay, showDay, toDay } from "@/lib/calendar-dates";
import { cn } from "@/lib/utils";

/**
 * One day, picked from a calendar or typed (#555).
 *
 * In and out as `YYYY-MM-DD`, or `""` for none — the same string the native
 * date input it replaces gave, so a caller changes its markup and nothing
 * else. The popover carries a field to type in as well as the grid: a date
 * three years back is quicker typed than paged to.
 */
export function DatePicker({
  id,
  value,
  onChange,
  min,
  max,
  placeholder = "Pick a date",
  clearable = true,
  display,
  footer,
  keepOpen = false,
  className,
  ...aria
}: {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  placeholder?: string;
  /** Whether "no date" is an answer. A due date can be cleared; a report's start cannot. */
  clearable?: boolean;
  /** What the trigger says instead of the day alone — a day with its time. */
  display?: string;
  /** More to choose under the calendar, such as a time. */
  footer?: ReactNode;
  /** Leave the popover open after a day is picked, when `footer` still has a choice in it. */
  keepOpen?: boolean;
  className?: string;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
  "aria-label"?: string;
}) {
  const [open, setOpen] = useState(false);
  const selected = parseDay(value);
  const earliest = parseDay(min);
  const latest = parseDay(max);

  function allowed(day: string): boolean {
    return (!min || day >= min) && (!max || day <= max);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          data-slot="date-picker"
          data-value={value}
          className={cn(
            "h-9 w-full justify-start px-3 font-normal",
            !selected && "text-muted-foreground",
            className,
          )}
          {...aria}
        >
          <CalendarDays className="text-muted-foreground" />
          <span className="truncate">
            {selected ? (display ?? showDay(value)) : placeholder}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-auto p-2"
        data-slot="date-picker-popover"
      >
        <DayField
          label="Date"
          value={value}
          accepts={allowed}
          onCommit={(day) => {
            onChange(day);
            if (!keepOpen) setOpen(false);
          }}
        />
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={selected ?? latest}
          startMonth={earliest}
          endMonth={latest}
          disabled={[
            ...(earliest ? [{ before: earliest }] : []),
            ...(latest ? [{ after: latest }] : []),
          ]}
          onSelect={(_, day) => {
            onChange(toDay(day));
            if (!keepOpen) setOpen(false);
          }}
        />
        {footer}
        {clearable && value !== "" ? (
          <div className="flex justify-end border-t pt-2">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                onChange("");
                setOpen(false);
              }}
            >
              Clear
            </Button>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
